import { memo, useCallback, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { PiMinus, PiSignOut } from 'react-icons/pi';
import { TbCaretDownFilled } from 'react-icons/tb';

import { cn } from '#/lib/utils';
import { isAuthEnabled } from '#/config/auth';
import { brand } from '#/config/brand';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import BrandLogo from '#/modules/infra/components/BrandLogo';
import asideMenuList from '#/modules/infra/navigation/asideMenuList';
import { filterAsideMenuByPermissions } from '#/modules/infra/navigation/filterAsideMenuByPermissions';
import {
  isAsideChildActive,
  isAsidePathMatch,
  resolveActiveAsideChild,
  resolveExpandedAsideParentIndex,
} from '#/modules/infra/navigation/asideNavUtils';
import { userLogout } from '#/modules/infra/redux/infraSlice';

function AsideChildLink({ child, pathname, siblings }) {
  const isActive = isAsideChildActive(pathname, child.to, siblings);

  if (child.enabled === false) {
    return (
      <div
        className="
          flex cursor-not-allowed items-center gap-2.5
          bg-primary-100 py-2.5 pl-12.5 text-gray-400
        "
      >
        <p className="flex items-center gap-1">
          <span>
            <PiMinus size={16} />
          </span>
          {child.title}
          <span className="text-xs">（即將推出）</span>
        </p>
      </div>
    );
  }

  return (
    <Link
      className={cn('aside_child_link', isActive && 'link_active')}
      to={child.to}
    >
      <p className="flex items-center gap-1">
        <span>
          <PiMinus size={16} />
        </span>
        {child.title}
      </p>
    </Link>
  );
}

AsideChildLink.propTypes = {
  child: PropTypes.shape({
    to: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    enabled: PropTypes.bool,
  }).isRequired,
  pathname: PropTypes.string.isRequired,
  siblings: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      enabled: PropTypes.bool,
    }),
  ).isRequired,
};

/**
 * Admin 左側導覽內容（logo／權限選單／accordion／登出）。
 * 桌面 rail 與手機 Sheet 共用，單一資料源。
 */
const AsideNavContent = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const permissions = useSelector((state) => state.infra.permissions);
  const visibleMenuList = useMemo(
    () => filterAsideMenuByPermissions(asideMenuList, permissions),
    [permissions],
  );
  const [expandedItems, setExpandedItems] = useState(() => {
    const parentIdx = resolveExpandedAsideParentIndex(pathname, asideMenuList);
    return parentIdx !== null ? { [parentIdx]: true } : {};
  });
  const dispatch = useDispatch();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();

  const handleLogout = useCallback(async () => {
    const ok = await confirm({
      title: '登出',
      message: '確定要登出嗎？',
      confirmText: '登出',
      cancelText: '取消',
    });
    if (ok) {
      dispatch(userLogout());
    }
  }, [confirm, dispatch]);

  function toggleAccordion(idx, hasChildren, enabled) {
    if (!hasChildren || enabled === false) {
      return;
    }

    setExpandedItems((prev) => {
      if (prev[idx]) {
        return {};
      }

      return { [idx]: true };
    });
  }

  return (
    <>
      <div className="aside_header">
        <BrandLogo className="aside_logo_icon" />
        <p className="aside-hidden_item text-xl font-bold text-gray-800">
          <span>{brand.companyName}</span>
          <span className="block text-sm font-medium text-gray-600">ERP 管理系統</span>
        </p>
      </div>
      <div className="grid min-h-0 grid-cols-1 grid-rows-[minmax(0,1fr)_auto] pb-7 pt-5">
        <ul className="flex min-h-0 flex-col gap-1 overflow-y-auto text-gray-600">
          {visibleMenuList.map((item, idx) => {
            const isDisabled = item.enabled === false;
            const hasActiveChild = item.children
              ? resolveActiveAsideChild(pathname, item.children) !== null
              : false;
            const isExpanded = Boolean(expandedItems[idx]);

            return (
              <li key={item.to}>
                {item.children ? (
                  <div
                    className={cn(
                      'aside_link',
                      hasActiveChild && 'link_active',
                      isExpanded && !hasActiveChild && 'link_expanded',
                      isDisabled && 'cursor-not-allowed opacity-60',
                    )}
                    onClick={() => toggleAccordion(idx, true, item.enabled)}
                  >
                    <div className="aside_title">
                      <span>{item.icon}</span>
                      <div className="aside-hidden_item-flex w-full items-center justify-between gap-2.5">
                        <p>
                          {item.title}
                          {isDisabled && <span className="ml-1 text-xs">（即將推出）</span>}
                        </p>
                        {!isDisabled && (
                          <span
                            className={cn(
                              'shortcut_transition-transform',
                              isExpanded && 'rotate-180',
                            )}
                          >
                            <TbCaretDownFilled size={20} />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : isDisabled ? (
                  <div className="aside_link cursor-not-allowed opacity-60">
                    <div className="aside_title">
                      <div className="flex items-center gap-2.5">
                        <span>{item.icon}</span>
                        <p className="aside-hidden_item">
                          {item.title}
                          <span className="ml-1 text-xs">（即將推出）</span>
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.to}
                    className={cn(
                      'aside_link',
                      isAsidePathMatch(pathname, item.to) && 'link_active',
                    )}
                  >
                    <div className="aside_title">
                      <div className="flex items-center gap-2.5">
                        <span>{item.icon}</span>
                        <p className="aside-hidden_item">{item.title}</p>
                      </div>
                    </div>
                  </Link>
                )}

                {item.children && !isDisabled && (
                  <div
                    className={cn(
                      'grid shortcut_transition-disclosure',
                      isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                    )}
                    aria-hidden={!isExpanded}
                  >
                    <ul
                      className="
                        aside-hidden_item mr-5 min-h-0 overflow-hidden bg-primary-100
                      "
                    >
                      {item.children.map((child) => (
                        <li key={child.to} className="bg-primary-100 text-gray-600">
                          <AsideChildLink
                            child={child}
                            pathname={pathname}
                            siblings={item.children}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <div className="mt-4 px-2">
          <p className="mb-2 text-center text-sm text-gray-500">v{import.meta.env.VITE_APP_VERSION}</p>
          {isAuthEnabled ? (
            <>
              <button
                type="button"
                aria-label="登出"
                className="
                  mx-auto flex w-3/4 items-center justify-center gap-2.5
                  rounded-sm bg-slate-600 py-3 text-white
                  transition-colors hover:bg-slate-700
                "
                onClick={handleLogout}
              >
                <PiSignOut size={22} aria-hidden />
                <span className="aside-hidden_item">登出</span>
              </button>
              {ConfirmDialogHost}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default memo(AsideNavContent);
